import {useState} from 'react';
import {Alert, Box, Button, Collapsible, DateInput, DatePicker, Dialog, Field, HStack, IconButton, Input, parseDate, Portal, Switch, VStack} from '@chakra-ui/react';
import {DialogCloseButton} from './ui/dialog-close-button';
import {Calendar, ChevronRight} from 'lucide-react';
import {toaster} from './ui/toaster';
import {useCreateAlbum} from '../drive/queries';
import {useEventSuggestions, usePhotographerSuggestions} from '../suggestions/useSuggestions';
import {TagListInput} from './TagListInput';
import {describeDriveError} from '../drive/driveApi';
import {AlbumTreeNode, albumTreeProblem, planAlbumTree} from '../domain/albumTree';
import {AlbumTreeEditor} from './AlbumTreeEditor';
import { HelpTooltip } from './HelpTooltip';

interface CreateAlbumModalProps {
    isOpen: boolean;
    onClose: () => void;
    driveId: string;
    /**
     * The folder to create the album in. Outside the year root (the drive itself) only a single-level
     * album can be made, without event tags: no event series or sub-albums.
     */
    parentId: string;
    onCreated?: (albumFolderId: string) => void;
}

function todayIsoDate(): string {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

export function CreateAlbumModal({isOpen, onClose, driveId, parentId, onCreated}: CreateAlbumModalProps) {
    const createAlbum = useCreateAlbum();
    const photographerSuggestions = usePhotographerSuggestions();
    const eventSuggestions = useEventSuggestions();
    const [title, setTitle] = useState('');
    const [dateStr, setDateStr] = useState(todayIsoDate());
    const [photographers, setPhotographers] = useState<string[]>([]);
    const [events, setEvents] = useState<string[]>([]);
    const [subAlbums, setSubAlbums] = useState<AlbumTreeNode[]>([]);
    const [treeOpen, setTreeOpen] = useState(false);
    // An event series is only the dated folder and its info.json; photographers and sub-albums don't apply.
    const [series, setSeries] = useState(false);
    // A sub-album of the folder being browsed: no date, so no date prefix on its folder name.
    const [subAlbum, setSubAlbum] = useState(false);

    const atYearRoot = parentId === driveId;
    const date = new Date(`${dateStr}T00:00:00`);
    const dateValue = [parseDate(dateStr)];
    // What applies here: the year-root-only inputs are hidden elsewhere (and the sub-album switch at the
    // year root), so their state is ignored there.
    const isSeries = atYearRoot && series;
    const isSubAlbum = !atYearRoot && subAlbum;
    const children = atYearRoot && !isSeries ? subAlbums : [];
    const subAlbumProblem = albumTreeProblem(children);
    const plan = planAlbumTree({
        date: isSubAlbum ? null : date,
        title,
        photographers,
        events: atYearRoot ? events : [],
        children,
        series: isSeries,
    });
    const previewFolderName = title.trim() ? plan.folderName : '';

    function reset() {
        setTitle('');
        setDateStr(todayIsoDate());
        setPhotographers([]);
        setEvents([]);
        setSubAlbums([]);
        setTreeOpen(false);
        setSeries(false);
        setSubAlbum(false);
    }

    function handleClose() {
        reset();
        onClose();
    }

    async function handleSubmit() {
        if (!title.trim() || subAlbumProblem) return;
        try {
            const folder = await createAlbum.mutateAsync({
                driveId,
                parentId,
                plan,
            });
            toaster.create({type: 'success', title: `Létrehozva: „${title.trim()}”`});
            onCreated?.(folder.id);
            handleClose();
        } catch (e) {
            toaster.create({type: 'error', title: describeDriveError(e)});
        }
    }

    return <Dialog.Root
        open={isOpen}
        onOpenChange={(e) => {
            if (!e.open) handleClose();
        }}
        size="lg"
    >
        <Portal>
        <Dialog.Backdrop/>
        <Dialog.Positioner>
        <Dialog.Content>
            <Dialog.Header><Dialog.Title>{isSubAlbum ? 'Új alalbum' : 'Új album'}</Dialog.Title></Dialog.Header>
            <DialogCloseButton/>
            <Dialog.Body>
                <VStack gap={4} align="stretch">
                    <Field.Root required>
                        <Field.Label>Cím <Field.RequiredIndicator/></Field.Label>
                        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="BME Jeges Est"/>
                    </Field.Root>
                    {!atYearRoot &&
                        <HStack gap={2}>
                            <Switch.Root
                                gap={2}
                                colorPalette="blue"
                                checked={subAlbum}
                                onCheckedChange={(e) => setSubAlbum(e.checked)}
                            >
                                <Switch.HiddenInput/>
                                <Switch.Control/>
                                <Switch.Label fontWeight="normal">Alalbum</Switch.Label>
                            </Switch.Root>
                            <HelpTooltip content="Dátum kihagyása a mappanévből." ariaLabel="Milyen egy alalbum?"/>
                        </HStack>
                    }
                    {!isSubAlbum &&
                        <Field.Root required>
                            {/* Typed like the browser's own date field (year, month and day segments), or picked in the calendar. */}
                            <DatePicker.Root
                                value={dateValue}
                                onValueChange={(e) => {
                                    if (e.value[0]) setDateStr(e.value[0].toString());
                                }}
                                locale="hu-HU"
                                startOfWeek={1}
                                positioning={{placement: 'bottom-end'}}
                            >
                                <DateInput.Root
                                    value={dateValue}
                                    onValueChange={(e) => {
                                        if (e.value[0]) setDateStr(e.value[0].toString());
                                    }}
                                    locale="hu-HU"
                                    granularity="day"
                                    shouldForceLeadingZeros
                                >
                                    <DateInput.Label>Dátum <Field.RequiredIndicator/></DateInput.Label>
                                    <DateInput.Control position="relative">
                                        <DateInput.Segments/>
                                        <DatePicker.Trigger asChild>
                                            <IconButton
                                                aria-label="Naptár megnyitása"
                                                size="xs"
                                                variant="ghost"
                                                position="absolute"
                                                right="2"
                                                top="50%"
                                                transform="translateY(-50%)"
                                            >
                                                <Calendar size={16}/>
                                            </IconButton>
                                        </DatePicker.Trigger>
                                    </DateInput.Control>
                                    <DateInput.HiddenInput/>
                                </DateInput.Root>
                                {/* Not portalled: the calendar has to stay inside the dialog's focus trap. */}
                                <DatePicker.Positioner>
                                    <DatePicker.Content>
                                        <DatePicker.View view="day">
                                            <DatePicker.Header/>
                                            <DatePicker.DayTable/>
                                        </DatePicker.View>
                                        <DatePicker.View view="month">
                                            <DatePicker.Header/>
                                            <DatePicker.MonthTable/>
                                        </DatePicker.View>
                                        <DatePicker.View view="year">
                                            <DatePicker.Header/>
                                            <DatePicker.YearTable/>
                                        </DatePicker.View>
                                    </DatePicker.Content>
                                </DatePicker.Positioner>
                            </DatePicker.Root>
                        </Field.Root>
                    }
                    {atYearRoot &&
                        <HStack gap={2}>
                            <Switch.Root
                                gap={2}
                                colorPalette="blue"
                                checked={series}
                                onCheckedChange={(e) => setSeries(e.checked)}
                            >
                                <Switch.HiddenInput/>
                                <Switch.Control/>
                                <Switch.Label fontWeight="normal">Rendezvénysorozat</Switch.Label>
                            </Switch.Root>
                            <HelpTooltip 
                                content="Gyűjtőalbum önállóan dátummal rendelkező albumoknak, pl. G7, Qpa." 
                                ariaLabel="Mi a rendezvénysorozat?"/>
                        </HStack>
                    }
                    <Field.Root disabled={isSeries}>
                        <Field.Label>Fotósok</Field.Label>
                        <TagListInput
                            value={photographers}
                            onChange={setPhotographers}
                            suggestions={photographerSuggestions}
                            placeholder="Fotós hozzáadása…"
                            disabled={isSeries}
                        />
                    </Field.Root>
                    {atYearRoot &&
                        <Field.Root>
                            <Field.Label>Rendezvénycímkék</Field.Label>
                            <TagListInput
                                value={events}
                                onChange={setEvents}
                                suggestions={eventSuggestions}
                                placeholder="Címke hozzáadása…"
                            />
                        </Field.Root>
                    }
                    {previewFolderName &&
                        <Box fontSize="sm" color="gray.500">
                            Mappa: {previewFolderName}/{plan.bins && '{web,archive,plazma}'}
                        </Box>
                    }
                    {/* Collapsed by default, so the simple, single-level album stays uncluttered. */}
                    {atYearRoot &&
                        <Collapsible.Root
                            open={treeOpen && !series}
                            onOpenChange={(e) => setTreeOpen(e.open)}
                            disabled={series}
                        >
                            <Collapsible.Trigger asChild>
                                <Button variant="ghost" size="sm" alignSelf="flex-start" px={1} className="group" disabled={series}>
                                    <Box as="span" transition="transform 0.2s" _groupExpanded={{transform: 'rotate(90deg)'}}>
                                        <ChevronRight size={16}/>
                                    </Box>
                                    Többszintű album{!series && subAlbums.length > 0 && ` (${subAlbums.length} alalbum)`}
                                </Button>
                            </Collapsible.Trigger>
                            <Collapsible.Content>
                                <Box pt={2}>
                                    <AlbumTreeEditor
                                        rootFolderName={previewFolderName}
                                        rootTitle={title}
                                        value={subAlbums}
                                        onChange={setSubAlbums}
                                    />
                                </Box>
                            </Collapsible.Content>
                        </Collapsible.Root>
                    }
                    {subAlbumProblem &&
                        <Alert.Root status="error" role="alert" borderRadius="md" fontSize="sm">
                            <Alert.Indicator/>
                            <Alert.Title>{subAlbumProblem}</Alert.Title>
                        </Alert.Root>
                    }
                </VStack>
            </Dialog.Body>
            <Dialog.Footer gap={0}>
                <Button variant="ghost" mr={3} onClick={handleClose}>Mégse</Button>
                <Button
                    colorPalette="blue"
                    onClick={handleSubmit}
                    disabled={!title.trim() || !!subAlbumProblem}
                    loading={createAlbum.isPending}
                >
                    Létrehozás
                </Button>
            </Dialog.Footer>
        </Dialog.Content>
        </Dialog.Positioner>
        </Portal>
    </Dialog.Root>;
}
